import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Wait for Firebase to restore auth session before making any request
function waitForAuth(): Promise<import('firebase/auth').User> {
  return new Promise((resolve, reject) => {
    // If already signed in, return immediately
    if (auth.currentUser) return resolve(auth.currentUser);
    // Wait up to 10 seconds for auth state to be restored
    const timer = setTimeout(() => {
      unsub();
      reject(new Error('Not authenticated'));
    }, 10000);
    const unsub = onAuthStateChanged(auth, user => {
      clearTimeout(timer);
      unsub();
      if (user) resolve(user);
      else reject(new Error('Not authenticated'));
    });
  });
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const user = await waitForAuth();
  const token = await user.getIdToken();
  const res = await fetch(`${BASE}/api${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`,'ngrok-skip-browser-warning': 'true' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || err.message || 'Request failed');
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export const api = {
  // Leads
  getLeads:       ()                        => req<any[]>('GET',    '/leads'),
  createLead:     (data: unknown)           => req<any>  ('POST',   '/leads', data),
  updateLead:     (id: string, d: unknown)  => req<any>  ('PUT',    `/leads/${id}`, d),
  deleteLead:     (id: string)              => req<void> ('DELETE', `/leads/${id}`),

  // Templates
  getTemplates:     ()                       => req<any[]>('GET',    '/templates'),
  createTemplate:   (data: unknown)          => req<any>  ('POST',   '/templates', data),
  updateTemplate:   (id: string, d: unknown) => req<any>  ('PUT',    `/templates/${id}`, d),
  deleteTemplate:   (id: string)             => req<void> ('DELETE', `/templates/${id}`),

  // Categories
  getCategories:    ()              => req<any[]>('GET',    '/categories'),
  createCategory:   (d: unknown)   => req<any>  ('POST',   '/categories', d),
  deleteCategory:   (id: string)   => req<void> ('DELETE', `/categories/${id}`),

  // Settings
  getSettings:  ()           => req<any>('GET',  '/settings'),
  saveSettings: (d: unknown) => req<any>('POST', '/settings', d),

  // Send
  sendEmail:    (d: unknown) => req<any>('POST', '/send/email', d),
  sendWhatsApp: (d: unknown) => req<any>('POST', '/send/whatsapp', d),
  quickSend:    (d: unknown) => req<any>('POST', '/send/quick', d),

  // Dashboard
  getDashboard: () => req<any>('GET', '/dashboard'),

  // Campaigns
  createCampaign: (d: unknown)  => req<any>('POST', '/campaigns', d),
  getCampaign:    (id: string)  => req<any>('GET',  `/campaigns/${id}`),
  getCampaigns:   ()            => req<any[]>('GET', '/campaigns'),

  // Import / Export
  exportLeads: async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();
    const res = await fetch(`${BASE}/api/leads/export`, {
      headers: { Authorization: `Bearer ${token}`,'ngrok-skip-browser-warning': 'true' },
    });
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'leadflow-leads.xlsx'; a.click();
    URL.revokeObjectURL(url);
  },

  importLeads: async (file: File): Promise<{ imported: number; skipped: number; errors: string[] }> => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${BASE}/api/leads/import`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`,'ngrok-skip-browser-warning': 'true' },
      body: form,
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Import failed'); }
    return res.json();
  },
};

// ── Student API ───────────────────────────────────────────────────────────────
export const sapi = {
  // CVs
  getCVs: () => req<any[]>('GET', '/student/cvs'),
  uploadCV: async (name: string, category: string, file: File): Promise<any> => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();
    const form = new FormData();
    form.append('name', name); form.append('category', category); form.append('file', file);
    const res = await fetch(`${BASE}/api/student/cvs`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }, body: form });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Upload failed'); }
    return res.json();
  },
  deleteCV: (id: string) => req<void>('DELETE', `/student/cvs/${id}`),

  // Cover Letters
  getCoverLetters: ()                              => req<any[]>('GET',  '/student/coverletters'),
  createCoverLetter: (name: string, body: string)  => req<any>('POST', '/student/coverletters', { name, body }),
  updateCoverLetter: (id: string, name: string, body: string) => req<any>('PUT', `/student/coverletters/${id}`, { name, body }),
  deleteCoverLetter: (id: string)                  => req<void>('DELETE', `/student/coverletters/${id}`),

  // Email Bodies
  getEmailBodies: ()                                               => req<any[]>('GET',  '/student/emailbodies'),
  createEmailBody: (name: string, subject: string, body: string)  => req<any>('POST', '/student/emailbodies', { name, subject, body }),
  updateEmailBody: (id: string, name: string, subject: string, body: string) => req<any>('PUT', `/student/emailbodies/${id}`, { name, subject, body }),
  deleteEmailBody: (id: string)                                    => req<void>('DELETE', `/student/emailbodies/${id}`),

  // Settings
  getStudentSettings:  ()       => req<any>('GET',  '/student/settings'),
  saveStudentSettings: (d: any) => req<any>('POST', '/student/settings', d),

  // Apply
  apply: (d: { cvId: string; coverLetterId: string; emailBodyId: string; companyEmail: string; companyName?: string; address?: string; managerName?: string; jobTitle?: string; admirables?: string | string[] }) => req<any>('POST', '/student/apply', d),

  // Admirations — reusable "what you admire about them" phrases
  getAdmirations:   ()             => req<any[]>('GET',    '/student/admirations'),
  createAdmiration: (text: string) => req<any>('POST',     '/student/admirations', { text }),
  deleteAdmiration: (id: string)   => req<void>('DELETE',  `/student/admirations/${id}`),

  // Job Titles — reusable titles, picked the same way as Admirations
  getJobTitles:   ()             => req<any[]>('GET',    '/student/jobtitles'),
  createJobTitle: (text: string) => req<any>('POST',     '/student/jobtitles', { text }),
  deleteJobTitle: (id: string)   => req<void>('DELETE',  `/student/jobtitles/${id}`),

  // AI Application — drafts an application from a pasted job description/email
  aiDraft: (text: string) => req<{
    companyName: string; companyEmail: string; address: string; managerName: string; jobTitle: string;
    cvId: string | null; coverLetterId: string | null; emailBodyId: string | null;
  }>('POST', '/student/ai-draft', { text }),

  // Companies
  getCompanies:   ()            => req<any[]>('GET',    '/student/companies'),
  deleteCompany:  (id: string)  => req<void>('DELETE',  `/student/companies/${id}`),

  // Find Jobs — RSS sources + discovered listings (draft-only, never auto-sent)
  getJobSources:    ()                          => req<any[]>('GET',    '/student/job-sources'),
  createJobSource:  (d: { name: string; feedUrl: string; keywords: string[] }) => req<any>('POST', '/student/job-sources', d),
  deleteJobSource:  (id: string)                => req<void> ('DELETE', `/student/job-sources/${id}`),
  refreshJobSource: (id: string)                => req<{ added: number }>('POST', `/student/job-sources/${id}/refresh`),
  getDiscoveredJobs:   ()           => req<any[]>('GET',    '/student/discovered-jobs'),
  dismissDiscoveredJob: (id: string) => req<void>('DELETE', `/student/discovered-jobs/${id}`),
};